/**
 * Today Dashboard Data Hook
 * Aggregates all data needed for the Today Dashboard using existing intelligence layer modules
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUnifiedWellnessScore } from './useUnifiedWellnessScore';
import { useILScore } from './useILScore';
import { useMentalWellnessScore } from './useMentalWellnessScore';
import { getTopInsight, getRecommendedActions } from '@/lib/intelligenceLayer';
import { fetchTimelineData, buildTimelineNarrative } from '@/lib/timelineIntelligence';
import { getLevelsForFeature } from '@/lib/fiveLevelFramework';

export interface TodayDashboardData {
  unifiedScore: {
    value: number;
    statusLabel: string;
    mentalScore: number | null;
    physicalScore: number | null;
    recoveryScore: number | null;
  };
  topInsight: {
    title: string;
    body: string;
    levelTags: string[];
  } | null;
  recommendedActions: Array<{
    id: string;
    label: string;
    description: string;
    actionType: "breathwork" | "protocol" | "checkin" | "talk_to_therappi" | "future_self" | "sanctuary" | "sleep" | "nutrition" | "cycle" | "meditation";
    route: string;
    priority: "primary" | "secondary";
  }>;
  timelineSummary: {
    hasRecentSanctuaryEntry: boolean;
    pastNote?: string;
    presentNote?: string;
    futureNote?: string;
  };
  futureSelf: {
    active: boolean;
    identityStatement?: string;
    currentPivotalStep?: string;
  };
  quickLinks: Array<{
    id: string;
    label: string;
    description: string;
    route: string;
  }>;
  greeting: string;
  displayName: string;
}

export function useTodayDashboardData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<TodayDashboardData | null>(null);

  const { score: unifiedScore, isLoading: unifiedLoading } = useUnifiedWellnessScore();
  const { data: ilData } = useILScore(7);
  const { data: mentalData } = useMentalWellnessScore(7);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          // Auth not ready yet - don't mark as loaded so we retry when auth settles
          return;
        }

        // Fetch profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('user_id', user.id)
          .single();

        const displayName = profile?.display_name?.split(' ')[0] || 'there';
        const userType = 'holistic' as const;

        const hour = new Date().getHours();
        let greeting = 'Good evening';
        if (hour < 12) greeting = 'Good morning';
        else if (hour < 18) greeting = 'Good afternoon';

        const insight = await getTopInsight(user.id).catch(() => null);
        const topInsight = insight ? {
          title: insight.insight_text.split('.')[0] + '.',
          body: insight.insight_text,
          levelTags: insight.related_levels || []
        } : null;

        const actions = await getRecommendedActions(user.id, userType).catch(() => [] as string[]);
        const recommendedActions = actions.map((action, idx) => ({
          id: `action-${idx}`,
          label: action,
          description: action,
          actionType: mapActionToType(action),
          route: mapActionToRoute(action),
          priority: (idx === 0 ? 'primary' : 'secondary') as 'primary' | 'secondary'
        })).slice(0, 3);

        const timelineData = await fetchTimelineData(user.id).catch(() => ({
          past: { sanctuary_entries: [] as unknown[] },
          future: { future_self_identity: null }
        } as any));
        const timelineSummary = {
          hasRecentSanctuaryEntry: timelineData.past.sanctuary_entries.length > 0,
          pastNote: timelineData.past.sanctuary_entries.length > 0
            ? `${timelineData.past.sanctuary_entries.length} entries in your Sanctuary`
            : undefined,
          presentNote: 'Track your day',
          futureNote: timelineData.future.future_self_identity
            ? 'Continue your journey'
            : 'Define your Future Self'
        };

        const futureSelf = { active: false, identityStatement: undefined, currentPivotalStep: undefined };
        const quickLinks = buildQuickLinks([]);
        const todayIL = ilData?.[0]?.il_score != null ? Math.round(ilData[0].il_score) : null;
        const todayMental = mentalData?.[0]?.mental_wellness_score != null ? Math.round(mentalData[0].mental_wellness_score) : null;

        setData({
          unifiedScore: {
            value: unifiedScore?.overall != null ? Math.round(unifiedScore.overall) : 0,
            statusLabel: unifiedScore?.status || 'loading',
            mentalScore: todayMental,
            physicalScore: todayIL,
            recoveryScore: unifiedScore?.breakdown?.recovery != null ? Math.round(unifiedScore.breakdown.recovery) : null
          },
          topInsight,
          recommendedActions,
          timelineSummary,
          futureSelf,
          quickLinks,
          greeting,
          displayName
        });

        hasLoadedRef.current = true;
        setLoading(false);
      } catch (err) {
        console.error('Error loading Today Dashboard data:', err);
        setError(err as Error);
        hasLoadedRef.current = true;
        setLoading(false);
      }
    };

    // SAFETY: don't wait forever on unifiedLoading; after 4s, proceed anyway
    if (!hasLoadedRef.current) {
      if (!unifiedLoading) {
        loadDashboardData();
      } else {
        const timer = setTimeout(() => {
          if (!hasLoadedRef.current) loadDashboardData();
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [unifiedLoading, unifiedScore, ilData, mentalData]);

  return { loading, error, data };
}

// Helper functions
function mapActionToType(action: string): "breathwork" | "protocol" | "checkin" | "talk_to_therappi" | "future_self" | "sanctuary" | "sleep" | "nutrition" | "cycle" | "meditation" {
  const lowerAction = action.toLowerCase();
  if (lowerAction.includes('breathwork') || lowerAction.includes('breathing')) return 'breathwork';
  if (lowerAction.includes('protocol')) return 'protocol';
  if (lowerAction.includes('check-in') || lowerAction.includes('check in')) return 'checkin';
  if (lowerAction.includes('therappi') || lowerAction.includes('talk')) return 'talk_to_therappi';
  if (lowerAction.includes('future self')) return 'future_self';
  if (lowerAction.includes('sanctuary')) return 'sanctuary';
  if (lowerAction.includes('sleep')) return 'sleep';
  if (lowerAction.includes('nutrition') || lowerAction.includes('food')) return 'nutrition';
  if (lowerAction.includes('cycle') || lowerAction.includes('menstrual')) return 'cycle';
  if (lowerAction.includes('meditat')) return 'meditation';
  return 'checkin';
}

function mapActionToRoute(action: string): string {
  const actionType = mapActionToType(action);
  const routeMap = {
    breathwork: '/telempathy-tools',
    protocol: '/inflammation',
    checkin: '/daily-check-in',
    talk_to_therappi: '/therapy',
    future_self: '/future-self-navigator',
    sanctuary: '/sanctuary',
    sleep: '/sleep-and-dreams',
    nutrition: '/nutrition',
    cycle: '/wellness-tracks',
    meditation: '/meditations'
  };
  return routeMap[actionType] || '/daily-check-in';
}

function buildQuickLinks(activeJourneys: any[]): Array<{
  id: string;
  label: string;
  description: string;
  route: string;
}> {
  const allLinks = [
    { id: 'checkin', label: 'Daily Check-In', description: 'Track your wellbeing', route: '/daily-check-in' },
    { id: 'balance', label: 'Balance Wheel', description: 'Connected self balance', route: '/daily-check-in' },
    { id: 'trends', label: 'Wellness Trends', description: 'Patterns over time', route: '/trends' },
    { id: 'therapy', label: 'Therappi AI', description: 'Talk to your AI companion', route: '/therapy' },
    { id: 'inflammation', label: 'Inflammation Hub', description: 'Body health insights', route: '/inflammation' },
    { id: 'sleep', label: 'Sleep & Dreams', description: 'Rest and recovery', route: '/sleep-and-dreams' },
    { id: 'sanctuary', label: 'Sanctuary', description: 'Healing timeline', route: '/sanctuary' },
    { id: 'cycle', label: 'Cycle & Lunar', description: 'Hormonal harmony', route: '/wellness-tracks' },
    { id: 'telempathy', label: 'Spiritual Tools', description: 'Intuitive practices', route: '/telempathy-tools' }
  ];

  return allLinks.slice(0, 8);
}
